/**
 * InterviewLanding - Welcome/consent page for public interviewees.
 * Collects optional name/email and requires consent before starting.
 */

import { useState } from 'react'

import type { PublicInterviewLandingData } from '@ispot/shared'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Clock, AlertTriangle, Loader2 } from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface InterviewLandingProps {
  landing: PublicInterviewLandingData
  onStart: (name?: string, email?: string, consentGiven: boolean) => void
  isLoading: boolean
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_CONSENT_TEXT =
  'I consent to this interview being recorded and analyzed.'

// ============================================================================
// COMPONENT
// ============================================================================

export function InterviewLanding({ landing, onStart, isLoading }: InterviewLandingProps) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [consentGiven, setConsentGiven] = useState(false)

  const isUnavailable = landing.isExpired || landing.isFull || !landing.isActive

  function handleStart() {
    if (!consentGiven || isUnavailable) return
    onStart(
      landing.collectName ? name || undefined : undefined,
      landing.collectEmail ? email || undefined : undefined,
      consentGiven,
    )
  }

  return (
    <div className="flex h-full items-center justify-center bg-warm-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-warm-400">
            iSpot Interview
          </p>
          <CardTitle className="font-display text-2xl font-semibold text-warm-800">
            {landing.planTitle}
          </CardTitle>
          {landing.estimatedDuration && (
            <div className="mt-3 flex justify-center">
              <Badge variant="outline" className="gap-1.5 border-warm-200 text-warm-500">
                <Clock className="h-3.5 w-3.5" />
                ~{landing.estimatedDuration} min
              </Badge>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Welcome message */}
          <p className="text-center text-sm leading-relaxed text-warm-600">
            {landing.welcomeMessage ??
              'Thank you for participating in this interview. Your insights are valuable to our research.'}
          </p>

          {/* Unavailable state */}
          {isUnavailable && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800">
                {landing.isExpired && 'This interview link has expired.'}
                {!landing.isExpired && landing.isFull && 'This interview has reached the maximum number of responses.'}
                {!landing.isExpired && !landing.isFull && 'This interview is not currently active.'}
              </p>
            </div>
          )}

          {/* Form fields (only when available) */}
          {!isUnavailable && (
            <>
              {landing.collectName && (
                <div className="space-y-1.5">
                  <label htmlFor="interviewee-name" className="text-sm font-medium text-warm-700">
                    Your name
                  </label>
                  <Input
                    id="interviewee-name"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="border-warm-200"
                  />
                </div>
              )}

              {landing.collectEmail && (
                <div className="space-y-1.5">
                  <label htmlFor="interviewee-email" className="text-sm font-medium text-warm-700">
                    Email address
                  </label>
                  <Input
                    id="interviewee-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-warm-200"
                  />
                </div>
              )}

              {/* Consent checkbox */}
              <div className="flex items-start gap-3 rounded-xl border border-warm-200 bg-warm-50 p-4">
                <Checkbox
                  id="consent"
                  checked={consentGiven}
                  onCheckedChange={(checked) => setConsentGiven(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="consent" className="cursor-pointer text-sm leading-relaxed text-warm-600">
                  {landing.consentText ?? DEFAULT_CONSENT_TEXT}
                </label>
              </div>

              {/* Start button */}
              <Button
                onClick={handleStart}
                disabled={!consentGiven || isLoading}
                className="w-full bg-business text-white hover:bg-business/90"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  'Start Interview'
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
